import React, { useEffect, useMemo, useRef, useState } from 'react';
import Loader from 'react-loader-spinner';
import {
  FiAlertCircle,
  FiEdit2,
  FiExternalLink,
  FiPlus,
  FiShuffle,
  FiStar,
  FiTrash2,
  FiX,
} from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { getTeamImageUrl } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { drawTeams, groupSizes, seedingWarnings } from '../utilities/leagueDraw';
import {
  removeTeamFromGroup,
  teamGroupIds,
  teamInGroup,
  toggleTeamGroup,
} from '../utilities/tournamentsApi';
import TeamPicker from './TeamPicker';
import defaultTeamLogo from '../images/defaultTeamLogo.png';
import '../styles/seasonRoster.css';

const GROUP_NAMES = [
  'Группа А', 'Группа Б', 'Группа В', 'Группа Г', 'Группа Д',
  'Группа Е', 'Группа Ж', 'Группа З', 'Группа И', 'Группа К',
];

export default function SeasonRoster({
  leagueType,
  teams,
  onTeamsChange,
  groupsState,
  onGroupsChange,
  champId,
  loading,
  rosterDirty,
  onSaveRoster,
  onResetRoster,
  groupsDirty,
  onSaveGroups,
  onResetGroups,
  saving,
  canWrite = true,
}) {
  const { t } = useI18n();
  const blockRef = useRef(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [groupError, setGroupError] = useState(null);
  const [seededIds, setSeededIds] = useState([]);

  useEffect(() => {
    if (!pickerOpen) return undefined;
    const node = blockRef.current;
    if (!node) return undefined;
    let frames = 0;
    let frameId = 0;
    const tick = () => {
      frames += 1;
      if (frames < 2) {
        frameId = requestAnimationFrame(tick);
        return;
      }
      node.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
    };
    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [pickerOpen]);

  const rosterIds = useMemo(() => new Set(teams.map((team) => team.team_id)), [teams]);
  const seededSet = new Set(seededIds.map(Number));
  const canDraw = groupsState.list.length > 0 && teams.length > 0;
  const isGroupTournament = Number(leagueType) === 2;

  const addTeam = (team) => {
    if (rosterIds.has(team.team_id)) return;
    onTeamsChange([...teams, team]);
  };

  const removeTeam = (team) => {
    onTeamsChange(teams.filter((item) => item.team_id !== team.team_id));
    if (teamGroupIds(groupsState.assignment, team.team_id).length) {
      const assignment = { ...groupsState.assignment };
      delete assignment[team.team_id];
      delete assignment[String(team.team_id)];
      onGroupsChange({ ...groupsState, assignment });
    }
  };

  const nextGroupName = () => {
    const used = new Set(groupsState.list.map((group) => group.name));
    return GROUP_NAMES.find((name) => !used.has(name)) || '';
  };

  const submitNewGroup = () => {
    const trimmed = (newGroupName || '').trim();
    if (!trimmed) {
      setGroupError(t('trGroupNameRequired'));
      return;
    }
    if (groupsState.list.some((group) => group.name === trimmed)) {
      setGroupError(t('trGroupNameExists'));
      return;
    }
    const nextId = groupsState.list.reduce((max, group) => Math.max(max, group.id), 0) + 1;
    onGroupsChange({
      ...groupsState,
      list: [...groupsState.list, {
        id: nextId,
        name: trimmed,
        priority: groupsState.list.length + 1,
      }],
    });
    setNewGroupName(null);
    setGroupError(null);
  };

  const submitRename = () => {
    const trimmed = renameValue.trim();
    if (!trimmed) {
      setGroupError(t('trGroupNameRequired'));
      return;
    }
    if (groupsState.list.some((group) => group.name === trimmed && group.id !== renamingId)) {
      setGroupError(t('trGroupNameExists'));
      return;
    }
    onGroupsChange({
      ...groupsState,
      list: groupsState.list.map((group) => (group.id === renamingId ? { ...group, name: trimmed } : group)),
    });
    setRenamingId(null);
    setGroupError(null);
  };

  const deleteGroup = (group) => {
    let assignment = { ...groupsState.assignment };
    Object.keys(assignment).forEach((teamId) => {
      if (teamInGroup(assignment, teamId, group.id)) {
        assignment = removeTeamFromGroup(assignment, teamId, group.id);
      }
    });
    onGroupsChange({
      list: groupsState.list.filter((item) => item.id !== group.id),
      assignment,
    });
  };

  const toggleAssign = (teamId, groupId) => {
    onGroupsChange({
      ...groupsState,
      assignment: toggleTeamGroup(groupsState.assignment, teamId, groupId),
    });
  };

  const toggleSeeded = (teamId) => {
    setSeededIds((prev) => (
      prev.some((id) => Number(id) === Number(teamId))
        ? prev.filter((id) => Number(id) !== Number(teamId))
        : [...prev, Number(teamId)]
    ));
  };

  const randomDistribute = () => {
    const { assignment } = drawTeams(teams, groupsState.list, seededIds);
    onGroupsChange({ ...groupsState, assignment });
  };

  const buckets = useMemo(() => {
    if (!isGroupTournament) return null;
    const sorted = [...groupsState.list].sort((a, b) => a.priority - b.priority);
    return {
      groups: sorted.map((group) => ({
        group,
        teams: teams.filter((team) => teamInGroup(groupsState.assignment, team.team_id, group.id)),
      })),
      unassigned: teams.filter((team) => !teamGroupIds(groupsState.assignment, team.team_id).length),
    };
  }, [isGroupTournament, teams, groupsState]);

  const teamLogo = (team) => (
    <img
      src={getTeamImageUrl(team.image) || defaultTeamLogo}
      alt=""
      onError={(event) => { event.target.src = defaultTeamLogo; }}
    />
  );

  const teamNameButton = (team) => (
    <Link
      to={`/teams_management/${team.team_id}`}
      className="sr-team-name"
      title={t('trOpenTeam')}
    >
      {seededSet.has(Number(team.team_id)) ? <FiStar className="sr-seed-mark" /> : null}
      <span>{team.name}</span>
      <FiExternalLink className="sr-team-link" />
    </Link>
  );

  const groupToggles = (team) => (
    <div className="sr-group-toggles" role="group" aria-label={t('trSeasonGroups')}>
      {groupsState.list.map((group) => {
        const active = teamInGroup(groupsState.assignment, team.team_id, group.id);
        return (
          <button
            type="button"
            key={group.id}
            className={`sr-group-chip${active ? ' is-on' : ''}`}
            disabled={!canWrite}
            aria-pressed={active}
            title={group.name}
            onClick={() => toggleAssign(team.team_id, group.id)}
          >
            {group.name}
          </button>
        );
      })}
    </div>
  );

  return (
    <>
      <section ref={blockRef} className="st-block sr-block">
        <div className="sr-block-head">
          <div>
            <h2 className="st-block-header">
              {t('trSeasonTeams')}
              {!loading ? <span className="sr-count">{teams.length}</span> : null}
              {rosterDirty ? <span className="sr-dirty">{t('trUnsaved')}</span> : null}
            </h2>
            <p className="sr-block-hint">{t('trSeasonTeamsHint')}</p>
          </div>
          {canWrite ? (
          <button type="button" className="st-btn st-btn-primary" onClick={() => setPickerOpen((value) => !value)}>
            <FiPlus /> {pickerOpen ? t('close') : t('trAddTeam')}
          </button>
          ) : null}
        </div>

        <div className={`sr-block-body ${pickerOpen ? 'is-picking' : ''}`}>
          {pickerOpen ? (
            <TeamPicker
              champId={champId}
              isPicked={(teamId) => rosterIds.has(teamId)}
              onPick={addTeam}
            />
          ) : null}

          <div className="sr-roster">
            {loading ? (
              <div className="st-loading">
                <Loader type="Oval" color="#3ad1a0" height={36} width={36} />
              </div>
            ) : teams.length === 0 ? (
              <p className="sr-empty-text">{t('trEmptySeasonTeams')}</p>
            ) : (
              <div className="sr-list">
                {teams.map((team) => (
                  <div key={team.team_id} className="sr-row">
                    {teamLogo(team)}
                    {teamNameButton(team)}
                    {canWrite ? (
                    <button
                      type="button"
                      className="sr-remove"
                      onClick={() => removeTeam(team)}
                      title={t('trRemoveFromSeason')}
                    >
                      <FiX />
                    </button>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {canWrite ? (
        <div className="st-actions sr-save-bar">
          <button type="button" className="st-btn st-btn-secondary" disabled={!rosterDirty || saving} onClick={onResetRoster}>
            {t('cancel')}
          </button>
          <button type="button" className="st-btn st-btn-primary" disabled={!rosterDirty || saving} onClick={onSaveRoster}>
            <span className="tr-btn-full">{t('trSaveTeams')}</span>
            <span className="tr-btn-short">{t('trSaveTeamsShort')}</span>
          </button>
        </div>
        ) : null}
      </section>

      {isGroupTournament && !loading ? (
        <section className="st-block sr-block">
          <div className="sr-block-head">
            <div>
              <h2 className="st-block-header">
                {t('trSeasonGroups')}
                <span className="sr-count">{groupsState.list.length}</span>
                {groupsDirty ? <span className="sr-dirty">{t('trUnsaved')}</span> : null}
              </h2>
              <p className="sr-block-hint">{t('trSeasonGroupsHint')}</p>
            </div>
            {canWrite && newGroupName === null ? (
              <div className="sr-toolbar">
                <button
                  type="button"
                  className="st-btn st-btn-secondary"
                  onClick={() => { setNewGroupName(nextGroupName()); setGroupError(null); }}
                >
                  <FiPlus /> {t('trCreateGroup')}
                </button>
                <button
                  type="button"
                  className="st-btn st-btn-secondary"
                  onClick={randomDistribute}
                  disabled={!canDraw}
                  title={canDraw ? '' : t('trDrawNeedTeams')}
                >
                  <FiShuffle /> {t('trRandomDraw')}
                </button>
              </div>
            ) : null}
          </div>

          <div className="sr-block-body">
            {newGroupName !== null ? (
              <>
                <div className="sr-note">
                  <FiAlertCircle />
                  <span>{t('trGroupNameNote')}</span>
                </div>
                <div className="sr-inline">
                  <input
                    type="text"
                    className="st-input"
                    value={newGroupName}
                    maxLength={100}
                    placeholder={t('trGroupNamePh')}
                    autoFocus
                    onChange={(event) => setNewGroupName(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') submitNewGroup();
                      if (event.key === 'Escape') { setNewGroupName(null); setGroupError(null); }
                    }}
                  />
                  <button type="button" className="st-btn st-btn-primary" onClick={submitNewGroup}>
                    {t('trCreateGroup')}
                  </button>
                  <button
                    type="button"
                    className="sr-icon-btn"
                    onClick={() => { setNewGroupName(null); setGroupError(null); }}
                  >
                    <FiX />
                  </button>
                </div>
              </>
            ) : null}

            {groupError ? <span className="st-field-error">{groupError}</span> : null}

            {groupsState.list.length === 0 ? (
              <div className="sr-note">{t('trEmptyGroups')}</div>
            ) : (
              <>
                {canDraw ? (
                  <p className="sr-draw-hint">
                    {t('trDrawSizes', { sizes: groupSizes(teams.length, groupsState.list.length).join(' / ') })}
                  </p>
                ) : null}

                <div className="sr-seed">
                  <div className="sr-seed-head">
                    <span className="sr-seed-title">
                      <FiStar />
                      {t('trSeededTeams')}
                      <span className="sr-count">{seededIds.length}</span>
                    </span>
                    {seededIds.length > 0 ? (
                      <button type="button" className="tr-inline-link" onClick={() => setSeededIds([])}>
                        {t('trClearSeeded')}
                      </button>
                    ) : null}
                  </div>
                  <p className="sr-block-hint">{t('trSeededHint')}</p>
                  {seedingWarnings(teams.length, groupsState.list.length, seededIds.length).map((warning) => (
                    <div key={warning} className="sr-warning">
                      <FiAlertCircle />
                      <span>{warning}</span>
                    </div>
                  ))}
                  <div className="sr-seed-list">
                    {teams.map((team) => {
                      const isSeeded = seededSet.has(Number(team.team_id));
                      return (
                        <button
                          type="button"
                          key={team.team_id}
                          className={`sr-seed-chip ${isSeeded ? 'is-on' : ''}`}
                          onClick={() => toggleSeeded(team.team_id)}
                        >
                          <FiStar />
                          {team.name}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="sr-groups">
                  {buckets.groups.map(({ group, teams: groupTeams }) => (
                    <div key={group.id} className="sr-group">
                      <div className="sr-group-head">
                        {renamingId === group.id ? (
                          <div className="sr-inline">
                            <input
                              type="text"
                              className="st-input"
                              value={renameValue}
                              maxLength={100}
                              autoFocus
                              onChange={(event) => setRenameValue(event.target.value)}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter') submitRename();
                                if (event.key === 'Escape') { setRenamingId(null); setGroupError(null); }
                              }}
                            />
                            <button type="button" className="st-btn st-btn-primary" onClick={submitRename}>
                              {t('save')}
                            </button>
                            <button
                              type="button"
                              className="sr-icon-btn"
                              onClick={() => { setRenamingId(null); setGroupError(null); }}
                            >
                              <FiX />
                            </button>
                          </div>
                        ) : (
                          <>
                            <span className="sr-group-name">
                              {group.name}
                              <span className="sr-count">{groupTeams.length}</span>
                            </span>
                            <div className="sr-group-actions">
                              {canWrite ? (
                              <>
                              <button
                                type="button"
                                className="sr-icon-btn"
                                onClick={() => {
                                  setRenamingId(group.id);
                                  setRenameValue(group.name);
                                  setGroupError(null);
                                }}
                                title={t('trRenameGroup')}
                              >
                                <FiEdit2 />
                              </button>
                              <button
                                type="button"
                                className="sr-icon-btn is-danger"
                                onClick={() => deleteGroup(group)}
                                title={t('trDeleteGroup')}
                              >
                                <FiTrash2 />
                              </button>
                              </>
                              ) : null}
                            </div>
                          </>
                        )}
                      </div>
                      {groupTeams.length === 0 ? (
                        <p className="sr-empty-text">{t('trEmptyGroupTeams')}</p>
                      ) : (
                        <div className="sr-list">
                          {groupTeams.map((team) => (
                            <div key={`${group.id}-${team.team_id}`} className="sr-row has-select">
                              {teamLogo(team)}
                              {teamNameButton(team)}
                              {groupToggles(team)}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}

                  <div className="sr-group">
                    <div className="sr-group-head">
                      <span className="sr-group-name">
                        {t('trUnassignedGroup')}
                        <span className="sr-count">{buckets.unassigned.length}</span>
                      </span>
                    </div>
                    {buckets.unassigned.length === 0 ? (
                      <p className="sr-empty-text">{t('trAllTeamsAssigned')}</p>
                    ) : (
                      <div className="sr-list">
                        {buckets.unassigned.map((team) => (
                          <div key={team.team_id} className="sr-row has-select">
                            {teamLogo(team)}
                            {teamNameButton(team)}
                            {groupToggles(team)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {canWrite ? (
          <div className="st-actions sr-save-bar">
            <button type="button" className="st-btn st-btn-secondary" disabled={!groupsDirty || saving} onClick={onResetGroups}>
              {t('cancel')}
            </button>
            <button type="button" className="st-btn st-btn-primary" disabled={!groupsDirty || saving} onClick={onSaveGroups}>
              <span className="tr-btn-full">{t('trSaveGroups')}</span>
              <span className="tr-btn-short">{t('trSaveGroupsShort')}</span>
            </button>
          </div>
          ) : null}
        </section>
      ) : null}
    </>
  );
}
